import { useState, useEffect, useMemo, useRef } from "react";

function formatDate(iso) {
  try {
    return new Intl.DateTimeFormat("en-IN", {
      day: "numeric", month: "short",
    }).format(new Date(iso));
  } catch { return ""; }
}

const NOTEBOOK_ICONS = [
  "menu_book", "science", "public", "functions",
  "history_edu", "biotech", "computer", "engineering",
  "psychology", "gavel", "stethoscope", "calculate",
];

const ICON_COLORS = [
  "var(--accent-2)", "var(--sky)", "var(--green)",
  "var(--purple)", "var(--amber)", "var(--rose)",
];

function getNotebookIcon(title = "", index = 0) {
  const t = title.toLowerCase();
  if (t.includes("physics") || t.includes("mechanics")) return "functions";
  if (t.includes("chem")) return "science";
  if (t.includes("bio")) return "biotech";
  if (t.includes("history")) return "history_edu";
  if (t.includes("math") || t.includes("calc")) return "calculate";
  if (t.includes("law") || t.includes("legal")) return "gavel";
  if (t.includes("med") || t.includes("anatomy")) return "stethoscope";
  if (t.includes("cs") || t.includes("code") || t.includes("comp")) return "computer";
  return NOTEBOOK_ICONS[index % NOTEBOOK_ICONS.length];
}

function getNotebookColor(index) {
  return ICON_COLORS[index % ICON_COLORS.length];
}

/* ── Per-user localStorage helpers for collections ──────────────────────────── */
function readJSON(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch { return fallback; }
}

export default function NotebookSidebar({
  notebooks = [], activeId, setActiveId,
  uploading, onUpload, onDelete, onRename,
  userId, isMobile, onClose,
}) {
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [hoverId, setHoverId] = useState(null);

  /* Inline notebook rename */
  const [renamingId, setRenamingId] = useState(null);
  const [renameDraft, setRenameDraft] = useState("");
  const renameInputRef = useRef(null);

  /* Drag & drop */
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverTarget, setDragOverTarget] = useState(null); // collectionId | "__ungrouped__" | null

  /* Collections (localStorage-persisted, per user) */
  const scope = userId || "guest";
  const collectionsKey = `sm_collections::${scope}`;
  const assignKey = `sm_nb_assign::${scope}`;

  const [collections, setCollections] = useState(() => readJSON(collectionsKey, []));
  const [assignments, setAssignments] = useState(() => readJSON(assignKey, {}));
  const [collapsed, setCollapsed] = useState({});           // { collectionId: true }
  const [editingCollectionId, setEditingCollectionId] = useState(null);
  const [collectionDraft, setCollectionDraft] = useState("");
  const collectionInputRef = useRef(null);

  // Reload persisted state when the user changes.
  useEffect(() => {
    setCollections(readJSON(collectionsKey, []));
    setAssignments(readJSON(assignKey, {}));
  }, [collectionsKey, assignKey]);

  // Persist on change.
  useEffect(() => {
    try { localStorage.setItem(collectionsKey, JSON.stringify(collections)); } catch { /* ignore quota errors */ }
  }, [collections, collectionsKey]);
  useEffect(() => {
    try { localStorage.setItem(assignKey, JSON.stringify(assignments)); } catch { /* ignore quota errors */ }
  }, [assignments, assignKey]);

  // Focus inputs when they appear.
  useEffect(() => { if (renamingId) renameInputRef.current?.select(); }, [renamingId]);
  useEffect(() => { if (editingCollectionId) collectionInputRef.current?.select(); }, [editingCollectionId]);

  /* ── Notebook grouping ── */
  const nbIndex = useMemo(() => {
    const m = {};
    notebooks.forEach((nb, i) => { m[nb.id] = i; });
    return m;
  }, [notebooks]);

  const validCollectionIds = useMemo(
    () => new Set(collections.map(c => c.id)),
    [collections]
  );

  const grouped = useMemo(() => {
    const byCollection = {};
    const ungrouped = [];
    for (const nb of notebooks) {
      const cid = assignments[nb.id];
      if (cid && validCollectionIds.has(cid)) {
        (byCollection[cid] ||= []).push(nb);
      } else {
        ungrouped.push(nb);
      }
    }
    return { byCollection, ungrouped };
  }, [notebooks, assignments, validCollectionIds]);

  /* ── Handlers ── */
  const handleDelete = (e, id) => {
    e.stopPropagation();
    if (confirmDelete === id) {
      onDelete(id);
      setConfirmDelete(null);
      // Clean up any collection assignment for the deleted notebook.
      setAssignments(prev => {
        if (!(id in prev)) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
    } else {
      setConfirmDelete(id);
      setTimeout(() => setConfirmDelete(null), 3000);
    }
  };

  const startRename = (e, nb) => {
    e.stopPropagation();
    setRenamingId(nb.id);
    setRenameDraft(nb.title || "");
  };
  const commitRename = () => {
    const id = renamingId;
    if (!id) return;
    const nb = notebooks.find(n => n.id === id);
    const clean = renameDraft.trim();
    if (clean && clean !== nb?.title) onRename?.(id, clean);
    setRenamingId(null);
    setRenameDraft("");
  };
  const cancelRename = () => { setRenamingId(null); setRenameDraft(""); };

  const assignTo = (notebookId, collectionId) => {
    setAssignments(prev => {
      const next = { ...prev };
      if (collectionId) next[notebookId] = collectionId;
      else delete next[notebookId];
      return next;
    });
  };

  const handleDrop = (e, collectionId) => {
    e.preventDefault();
    const notebookId = e.dataTransfer.getData("text/plain") || draggingId;
    setDragOverTarget(null);
    setDraggingId(null);
    if (notebookId) assignTo(notebookId, collectionId);
  };

  const createCollection = () => {
    const id = `c_${Date.now().toString(36)}`;
    setCollections(prev => [...prev, { id, name: "New Collection" }]);
    setEditingCollectionId(id);
    setCollectionDraft("New Collection");
  };
  const commitCollectionName = () => {
    const id = editingCollectionId;
    if (!id) return;
    const clean = collectionDraft.trim().slice(0, 40) || "Untitled";
    setCollections(prev => prev.map(c => (c.id === id ? { ...c, name: clean } : c)));
    setEditingCollectionId(null);
    setCollectionDraft("");
  };
  const deleteCollection = (id) => {
    // Unassign its notebooks (they return to Ungrouped); notebooks are not deleted.
    setAssignments(prev => {
      const next = { ...prev };
      for (const k of Object.keys(next)) if (next[k] === id) delete next[k];
      return next;
    });
    setCollections(prev => prev.filter(c => c.id !== id));
  };
  const toggleCollapse = (id) =>
    setCollapsed(prev => ({ ...prev, [id]: !prev[id] }));

  /* ── Render one notebook row ── */
  const renderNotebook = (nb) => {
    const i = nbIndex[nb.id] ?? 0;
    const isActive = nb.id === activeId;
    const isHovered = hoverId === nb.id;
    const isRenaming = renamingId === nb.id;
    const icon = getNotebookIcon(nb.title, i);
    const color = getNotebookColor(i);

    return (
      <div
        key={nb.id}
        draggable={!isRenaming}
        onDragStart={e => {
          e.dataTransfer.setData("text/plain", nb.id);
          e.dataTransfer.effectAllowed = "move";
          setDraggingId(nb.id);
        }}
        onDragEnd={() => { setDraggingId(null); setDragOverTarget(null); }}
        onClick={() => { if (!isRenaming) setActiveId(nb.id); }}
        onMouseEnter={() => setHoverId(nb.id)}
        onMouseLeave={() => setHoverId(null)}
        style={{
          display: "flex", alignItems: "center", gap: 9,
          padding: "8px 9px",
          borderRadius: "var(--radius-md)",
          cursor: isRenaming ? "default" : "pointer",
          marginBottom: 2,
          transition: "all var(--transition)",
          background: isActive ? "var(--accent-soft)" : isHovered ? "var(--surface-2)" : "transparent",
          border: "1px solid",
          borderColor: isActive ? "rgba(99,102,241,0.22)" : "transparent",
          position: "relative",
          opacity: draggingId === nb.id ? 0.5 : 1,
        }}
      >
        {/* Icon */}
        <div style={{
          width: 30, height: 30, borderRadius: 8, flexShrink: 0,
          background: isActive ? "rgba(99,102,241,0.15)" : "var(--surface-3)",
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "all var(--transition)",
        }}>
          <span className="material-symbols-rounded" style={{
            fontSize: 16,
            color: isActive ? "var(--accent-2)" : color,
            fontVariationSettings: "'FILL' 1",
          }}>
            {icon}
          </span>
        </div>

        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {isRenaming ? (
            <input
              ref={renameInputRef}
              value={renameDraft}
              autoFocus
              maxLength={80}
              onClick={e => e.stopPropagation()}
              onChange={e => setRenameDraft(e.target.value)}
              onBlur={commitRename}
              onKeyDown={e => {
                if (e.key === "Enter") { e.preventDefault(); commitRename(); }
                else if (e.key === "Escape") { e.preventDefault(); cancelRename(); }
              }}
              style={{
                width: "100%", fontSize: 12.5, fontWeight: 600,
                padding: "2px 6px", borderRadius: 6,
                border: "1px solid var(--accent)", background: "var(--surface)",
                color: "var(--ink)", outline: "none",
              }}
            />
          ) : (
            <>
              <div style={{
                fontSize: 12.5, fontWeight: 600,
                color: isActive ? "var(--accent-2)" : "var(--ink)",
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              }}>
                {nb.title}
              </div>
              <div style={{ fontSize: 10.5, color: "var(--ink-3)", marginTop: 1 }}>
                {nb.page_count}p · {formatDate(nb.created_at)}
              </div>
            </>
          )}
        </div>

        {/* Row actions */}
        {!isRenaming && (isHovered || confirmDelete === nb.id) && (
          <div style={{ display: "flex", gap: 3, flexShrink: 0 }}>
            <button
              onClick={e => startRename(e, nb)}
              title="Rename"
              style={{
                width: 24, height: 24, borderRadius: 6,
                background: "var(--surface-3)", color: "var(--ink-3)",
                display: "flex", alignItems: "center", justifyContent: "center",
                border: "none", cursor: "pointer", transition: "all var(--transition)",
              }}
            >
              <span className="material-symbols-rounded" style={{ fontSize: 13 }}>edit</span>
            </button>
            <button
              onClick={e => handleDelete(e, nb.id)}
              title={confirmDelete === nb.id ? "Click again to confirm" : "Delete"}
              style={{
                width: 24, height: 24, borderRadius: 6,
                background: confirmDelete === nb.id ? "var(--rose)" : "var(--surface-3)",
                color: confirmDelete === nb.id ? "white" : "var(--ink-3)",
                display: "flex", alignItems: "center", justifyContent: "center",
                border: "none", cursor: "pointer", transition: "all var(--transition)",
              }}
            >
              <span className="material-symbols-rounded" style={{ fontSize: 13 }}>
                {confirmDelete === nb.id ? "delete_forever" : "delete"}
              </span>
            </button>
          </div>
        )}
      </div>
    );
  };

  /* ── Render a collection group ── */
  const renderCollection = (col) => {
    const items = grouped.byCollection[col.id] || [];
    const isCollapsed = collapsed[col.id];
    const isEditing = editingCollectionId === col.id;
    const isDragOver = dragOverTarget === col.id;

    return (
      <div
        key={col.id}
        onDragOver={e => { e.preventDefault(); setDragOverTarget(col.id); }}
        onDragLeave={() => setDragOverTarget(prev => (prev === col.id ? null : prev))}
        onDrop={e => handleDrop(e, col.id)}
        style={{
          marginBottom: 6,
          borderRadius: "var(--radius-md)",
          border: "1px solid",
          borderColor: isDragOver ? "var(--accent)" : "transparent",
          background: isDragOver ? "var(--accent-soft)" : "transparent",
          transition: "all var(--transition)",
        }}
      >
        {/* Collection header */}
        <div
          className="collection-header"
          style={{
            display: "flex", alignItems: "center", gap: 7,
            padding: "6px 8px", borderRadius: "var(--radius-md)",
            cursor: "pointer",
          }}
          onClick={() => !isEditing && toggleCollapse(col.id)}
        >
          <span className="material-symbols-rounded" style={{
            fontSize: 15, color: "var(--ink-3)", flexShrink: 0,
            transform: isCollapsed ? "rotate(-90deg)" : "none",
            transition: "transform 0.15s ease",
          }}>
            expand_more
          </span>
          <span className="material-symbols-rounded" style={{
            fontSize: 15, color: "var(--amber)", flexShrink: 0, fontVariationSettings: "'FILL' 1",
          }}>
            folder
          </span>

          {isEditing ? (
            <input
              ref={collectionInputRef}
              value={collectionDraft}
              autoFocus
              maxLength={40}
              onClick={e => e.stopPropagation()}
              onChange={e => setCollectionDraft(e.target.value)}
              onBlur={commitCollectionName}
              onKeyDown={e => {
                if (e.key === "Enter") { e.preventDefault(); commitCollectionName(); }
                else if (e.key === "Escape") { e.preventDefault(); commitCollectionName(); }
              }}
              style={{
                flex: 1, minWidth: 0, fontSize: 11.5, fontWeight: 700,
                padding: "2px 6px", borderRadius: 6,
                border: "1px solid var(--accent)", background: "var(--surface)",
                color: "var(--ink)", outline: "none",
              }}
            />
          ) : (
            <span style={{
              flex: 1, minWidth: 0, fontSize: 11.5, fontWeight: 700,
              color: "var(--ink-2)", textTransform: "uppercase", letterSpacing: "0.04em",
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>
              {col.name}
            </span>
          )}

          <span style={{
            fontSize: 10.5, fontWeight: 600, color: "var(--ink-3)",
            background: "var(--surface-3)", padding: "0px 6px", borderRadius: "var(--radius-full)",
            flexShrink: 0,
          }}>
            {items.length}
          </span>

          {!isEditing && (
            <>
              <button
                onClick={e => {
                  e.stopPropagation();
                  setEditingCollectionId(col.id);
                  setCollectionDraft(col.name);
                }}
                title="Rename collection"
                className="collection-action"
                style={collectionActionStyle}
              >
                <span className="material-symbols-rounded" style={{ fontSize: 13 }}>edit</span>
              </button>
              <button
                onClick={e => { e.stopPropagation(); deleteCollection(col.id); }}
                title="Delete collection (keeps notebooks)"
                className="collection-action"
                style={collectionActionStyle}
              >
                <span className="material-symbols-rounded" style={{ fontSize: 13 }}>delete</span>
              </button>
            </>
          )}
        </div>

        {/* Collection body */}
        {!isCollapsed && (
          <div style={{ paddingLeft: 8 }}>
            {items.length === 0 ? (
              <div style={{
                fontSize: 11, color: "var(--ink-3)", fontStyle: "italic",
                padding: "6px 10px",
              }}>
                Drag notebooks here
              </div>
            ) : (
              items.map(renderNotebook)
            )}
          </div>
        )}
      </div>
    );
  };

  const hasCollections = collections.length > 0;
  const ungroupedDragOver = dragOverTarget === "__ungrouped__";

  return (
    <aside className="notebook-sidebar" style={{
      width: "var(--sidebar-w)",
      height: "100%",
      background: "var(--surface)",
      borderRight: "1px solid var(--border)",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      flexShrink: 0,
    }}>
      {/* Header */}
      <div style={{
        padding: "12px 12px 10px",
        borderBottom: "1px solid var(--border)",
        flexShrink: 0,
      }}>
        <div style={{
          display: "flex", alignItems: "center",
          justifyContent: "space-between", marginBottom: 10,
        }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Notebooks
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{
              fontSize: 11, fontWeight: 600,
              color: "var(--accent-2)",
              background: "var(--accent-soft)",
              padding: "1px 7px",
              borderRadius: "var(--radius-full)",
            }}>
              {notebooks.length}
            </span>
            {isMobile && (
              <button
                onClick={onClose}
                className="btn btn-ghost btn-icon-sm"
                title="Close"
              >
                <span className="material-symbols-rounded" style={{ fontSize: 18 }}>close</span>
              </button>
            )}
          </div>
        </div>

        <button
          onClick={onUpload}
          disabled={uploading}
          style={{
            width: "100%",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
            padding: "8px 12px",
            border: "1px dashed rgba(99,102,241,0.35)",
            borderRadius: "var(--radius-md)",
            background: "var(--accent-soft)",
            color: "var(--accent-2)",
            fontSize: 12, fontWeight: 600,
            cursor: uploading ? "wait" : "pointer",
            transition: "all var(--transition)",
            opacity: uploading ? 0.6 : 1,
          }}
          onMouseEnter={e => { if (!uploading) e.currentTarget.style.borderColor = "rgba(99,102,241,0.6)"; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(99,102,241,0.35)"; }}
        >
          {uploading ? (
            <><div className="spinner spinner-sm" />&nbsp;Processing…</>
          ) : (
            <>
              <span className="material-symbols-rounded" style={{ fontSize: 16, fontVariationSettings: "'FILL' 1" }}>
                upload_file
              </span>
              Import PDF / Doc
            </>
          )}
        </button>
      </div>

      {/* Collections toolbar */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "8px 12px 4px", flexShrink: 0,
      }}>
        <span style={{ fontSize: 10.5, fontWeight: 600, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Collections
        </span>
        <button
          onClick={createCollection}
          title="New collection"
          style={{
            display: "flex", alignItems: "center", gap: 4,
            padding: "2px 8px", borderRadius: "var(--radius-full)",
            border: "1px solid var(--border-2)", background: "var(--surface-2)",
            color: "var(--ink-2)", fontSize: 11, fontWeight: 600, cursor: "pointer",
            transition: "all var(--transition)",
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--accent)"; e.currentTarget.style.color = "var(--accent-2)"; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border-2)"; e.currentTarget.style.color = "var(--ink-2)"; }}
        >
          <span className="material-symbols-rounded" style={{ fontSize: 14 }}>create_new_folder</span>
          New
        </button>
      </div>

      {/* Notebook list + collections */}
      <div style={{ flex: 1, overflowY: "auto", padding: "4px 8px 8px" }}>
        {notebooks.length === 0 ? (
          <div style={{
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            padding: "32px 16px", gap: 10, textAlign: "center",
          }}>
            <div style={{
              width: 44, height: 44, borderRadius: 12,
              background: "var(--surface-3)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <span className="material-symbols-rounded" style={{ fontSize: 22, color: "var(--ink-3)" }}>
                folder_open
              </span>
            </div>
            <div style={{ fontSize: 12, color: "var(--ink-3)", lineHeight: 1.5 }}>
              No notebooks yet.<br/>Import a PDF to start.
            </div>
          </div>
        ) : (
          <>
            {/* Collections */}
            {collections.map(renderCollection)}

            {/* Ungrouped */}
            <div
              onDragOver={e => { e.preventDefault(); setDragOverTarget("__ungrouped__"); }}
              onDragLeave={() => setDragOverTarget(prev => (prev === "__ungrouped__" ? null : prev))}
              onDrop={e => handleDrop(e, null)}
              style={{
                borderRadius: "var(--radius-md)",
                border: "1px solid",
                borderColor: ungroupedDragOver ? "var(--accent)" : "transparent",
                background: ungroupedDragOver ? "var(--accent-soft)" : "transparent",
                transition: "all var(--transition)",
                marginTop: hasCollections ? 4 : 0,
              }}
            >
              {hasCollections && (
                <div style={{
                  display: "flex", alignItems: "center", gap: 7,
                  padding: "6px 8px",
                }}>
                  <span className="material-symbols-rounded" style={{ fontSize: 15, color: "var(--ink-3)", flexShrink: 0 }}>
                    inbox
                  </span>
                  <span style={{
                    flex: 1, fontSize: 11.5, fontWeight: 700, color: "var(--ink-3)",
                    textTransform: "uppercase", letterSpacing: "0.04em",
                  }}>
                    Ungrouped
                  </span>
                  <span style={{
                    fontSize: 10.5, fontWeight: 600, color: "var(--ink-3)",
                    background: "var(--surface-3)", padding: "0px 6px", borderRadius: "var(--radius-full)",
                  }}>
                    {grouped.ungrouped.length}
                  </span>
                </div>
              )}
              <div style={{ paddingLeft: hasCollections ? 8 : 0 }}>
                {grouped.ungrouped.map(renderNotebook)}
                {hasCollections && grouped.ungrouped.length === 0 && (
                  <div style={{
                    fontSize: 11, color: "var(--ink-3)", fontStyle: "italic",
                    padding: "6px 10px",
                  }}>
                    Drag notebooks here to remove from a collection
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Footer hint */}
      <div style={{
        padding: "10px 12px",
        borderTop: "1px solid var(--border)",
        flexShrink: 0,
      }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 6,
          fontSize: 11, color: "var(--ink-3)",
        }}>
          <span className="material-symbols-rounded" style={{ fontSize: 13, fontVariationSettings: "'FILL' 1", color: "var(--green)" }}>
            drag_indicator
          </span>
          Drag a notebook into a collection
        </div>
      </div>
    </aside>
  );
}

const collectionActionStyle = {
  width: 22, height: 22, borderRadius: 6, flexShrink: 0,
  background: "transparent", color: "var(--ink-3)",
  display: "flex", alignItems: "center", justifyContent: "center",
  border: "none", cursor: "pointer", transition: "all var(--transition)",
};
